import {
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@bleu.builders/ui";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { DownloadCloudIcon } from "lucide-react";
import React, { useState } from "react";
import useSWR from "swr";

import { thesisUpdates } from "#/api/all";
import { formatDate } from "#/lib/formatDate";
import { client } from "#/utils/api-client";

import { DownloadNowButton } from "./DownloadThesisButton";

const UpdateDetails = ({ update }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      className="px-0"
    >
      <CollapsibleTrigger className="w-full select-text rounded-lg">
        <div
          className={cn(
            "px-2 bg-muted dark:border-2 font-normal flex justify-between items-center text-left w-full",
            {
              "rounded-lg border": !isExpanded,
              "rounded-t-lg border-t border-l border-r": isExpanded,
            },
          )}
        >
          <span className="p-1.5 font-bold text-base underline-offset-1">
            {formatDate(update.created_at)}
          </span>
          <div className="ml-2">
            {isExpanded ? (
              <PlusCircledIcon className="text-primary size-4 font-bold" />
            ) : (
              <PlusCircledIcon className="text-muted-foreground size-4 font-bold" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div
          className={cn("px-2 bg-muted dark:border-2", {
            "rounded-b-lg border-b border-l border-r": isExpanded,
          })}
        >
          <div className="text-sm font-light p-1.5">{update.summary}</div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const DownloadUpdatedThesisModal = ({
  children,
  thesisId,
  thesisTitle,
  optToDownloadOldKit = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data, error, isLoading } = useSWR(
    [thesisUpdates.index.path({ thesis_id: thesisId }), {}],
    async ([url]) => await client(url),
    {
      keepPreviousData: true,
    },
  );

  if (isLoading) return <div>Carregando...</div>;
  if (error) return <div>Erro ao carregar</div>;

  const updates = data?.data;

  const alertCaption = optToDownloadOldKit
    ? "Somente o download do kit da tese atualizada debitará um crédito da sua organização."
    : "Debitaremos um crédito da sua organização.";

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px] px-8">
        <DialogHeader className="justify-center">
          <DialogTitle className="flex gap-2 items-center justify-start text-center">
            <span className="text-primary uppercase">Atualizações</span>
            <DownloadCloudIcon className="text-primary size-5 font-bold" />
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Confira o que mudou desde a última versão que você adquiriu.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 overflow-y-scroll max-h-44">
          {updates?.map((update, index) => (
            <UpdateDetails key={index} update={update} />
          ))}
        </div>
        <DialogFooter>
          <div className="w-full mt-2 text-center">
            <div className="flex gap-2 items-center justify-center">
              <DownloadNowButton
                thesisId={thesisId}
                thesisTitle={thesisTitle}
                setIsOpen={setIsOpen}
                label="Baixar kit da tese atualizada"
                downloadLatestVersion={true}
              />
              {optToDownloadOldKit && (
                <DownloadNowButton
                  thesisId={thesisId}
                  thesisTitle={thesisTitle}
                  setIsOpen={setIsOpen}
                  className="bg-primary hover:bg-primary/80"
                  label="Baixar kit antigo"
                  downloadLatestVersion={false}
                />
              )}
            </div>
            <p className="mt-2 text-muted-foreground text-sm text-center italic">
              {alertCaption}
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
